import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import "./AISolutions.css";

const siteUrl = "https://www.samsoftix.com";
const pageUrl = `${siteUrl}/AI_Solutions_SamSoftix`;
const businessId = `${siteUrl}/#business`;

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: "SamSoftix",
      url: siteUrl,
      description:
        "SamSoftix provides AI, web, mobile app, software, ecommerce, SEO and digital solutions for clients worldwide."
    },

    {
      "@type": ["LocalBusiness", "ProfessionalService"],
      "@id": businessId,
      name: "SamSoftix",
      url: siteUrl,
      telephone: "+92-332-0472184",
      email: "info@samsoftix.com",
      description:
        "SamSoftix is a software house and training center based in Pakistan, providing AI development, automation, chatbots, machine learning, software and digital solutions for clients worldwide.",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Wan",
        addressRegion: "Punjab",
        postalCode: "51010",
        addressCountry: "PK"
      },
      areaServed: [
        "Worldwide",
        "Pakistan",
        "Wan",
        "Sambrial",
        "Sialkot",
        "Punjab"
      ],
      sameAs: [
        "https://www.facebook.com/samsoftix",
        "https://www.linkedin.com/company/samsoftix",
        "https://www.instagram.com/samsoftix"
      ]
    },

    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name:
        "AI Solutions & AI Development Company | SamSoftix",
      description:
        "SamSoftix provides AI development, AI automation, chatbots, machine learning, AI integration and custom intelligent software solutions for clients worldwide.",
      isPartOf: {
        "@id": `${siteUrl}/#website`
      },
      about: {
        "@id": businessId
      },
      inLanguage: "en-PK"
    },

    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "AI Solutions",
          item: pageUrl
        }
      ]
    },

    {
      "@type": "Service",
      name: "AI Solutions and AI Development",
      provider: {
        "@id": businessId
      },
      serviceType: [
        "AI Development",
        "Artificial Intelligence Solutions",
        "AI Chatbot Development",
        "AI Automation",
        "Machine Learning",
        "AI Integration",
        "Business Process Automation",
        "AI Software Development"
      ],
      areaServed: "Worldwide",
      url: pageUrl,
      description:
        "AI development and automation services for businesses, startups and organizations worldwide."
    },

    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name:
            "Does SamSoftix provide AI development services worldwide?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes. SamSoftix provides remote AI development and automation services for businesses, startups, organizations and project owners worldwide."
          }
        },

        {
          "@type": "Question",
          name:
            "What AI solutions does SamSoftix develop?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "SamSoftix can develop AI chatbots, virtual assistants, business automation systems, machine learning solutions, AI integrations and custom AI-powered software according to project requirements."
          }
        },

        {
          "@type": "Question",
          name:
            "Can AI be integrated into an existing business system?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes. AI solutions can be integrated with existing websites, applications, APIs, databases, CRM systems, ERP systems and other business platforms when the required technical access and integration methods are available."
          }
        },

        {
          "@type": "Question",
          name:
            "How much does AI development cost?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "AI development cost depends on the project scope, AI functionality, integrations, data requirements, infrastructure and complexity. SamSoftix provides project-specific quotations after reviewing the requirements."
          }
        },

        {
          "@type": "Question",
          name:
            "Can SamSoftix build an AI chatbot for my business?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes. SamSoftix can develop custom AI chatbot solutions for websites, applications and business workflows based on the required functionality and available data or services."
          }
        }
      ]
    }
  ]
};

function AISolutions() {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate("/serviceBooking");
  };

  return (
    <main className="ai-page">
      <Helmet>
        <html lang="en-PK" />

        <title>
          AI Solutions & AI Development Company | SamSoftix
        </title>

        <meta
          name="description"
          content="SamSoftix provides AI development, AI automation, chatbots, machine learning, AI integration and custom intelligent software solutions for clients worldwide."
        />

        <meta
          name="keywords"
          content="AI solutions, AI development company, artificial intelligence development, AI automation, AI chatbot development, machine learning solutions, AI integration, AI software development, AI company Pakistan, AI development Pakistan, AI services Sambrial, AI services Sialkot, AI development worldwide, remote AI development"
        />

        <meta name="author" content="SamSoftix" />

        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />

        <link rel="canonical" href={pageUrl} />

        <meta name="geo.region" content="PK-PB" />

        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        <meta
          property="og:title"
          content="AI Solutions & AI Development Company | SamSoftix"
        />

        <meta
          property="og:description"
          content="AI development, automation, chatbots, machine learning and AI integration services from SamSoftix. Based in Pakistan and serving clients worldwide."
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="SamSoftix" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="AI Solutions & AI Development | SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Custom AI development, automation, chatbots and intelligent software solutions for clients worldwide."
        />

        <meta name="theme-color" content="#06111f" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="ai-hero">
        <div className="ai-container">
          <span className="ai-kicker">
            ARTIFICIAL INTELLIGENCE • AUTOMATION • MACHINE LEARNING
          </span>

          <h1>
            AI Solutions
            <span> for Businesses Worldwide</span>
          </h1>

          <p>
            SamSoftix develops AI-powered solutions for businesses, startups,
            organizations and digital products. From AI chatbots and
            automation to machine learning and intelligent software
            integrations, we help businesses explore practical AI solutions
            for their specific requirements.
          </p>

          <div className="ai-location-tags">
            <span>Worldwide</span>
            <span>Remote AI Development</span>
            <span>Pakistan</span>
            <span>Sambrial</span>
            <span>Sialkot</span>
          </div>

          <div className="ai-hero-actions">
            <button
              onClick={handleBooking}
              className="ai-btn ai-btn-primary"
            >
              Discuss Your AI Project
            </button>

            <button
              onClick={handleBooking}
              className="ai-btn ai-btn-secondary"
            >
              Request a Quote
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          INTRO
      ===================================================== */}

      <section className="ai-section">
        <div className="ai-container">
          <div className="ai-section-heading">
            <span className="ai-section-label">
              AI SOFTWARE DEVELOPMENT
            </span>

            <h2>
              Practical AI Solutions Built Around Your Business
            </h2>

            <p>
              AI can support customer service, business workflows, data
              processing, content operations, internal systems and many other
              digital processes. We develop solutions based on the actual
              requirements, available data and technical environment of each
              project.
            </p>
          </div>

          <div className="ai-grid">
            <article className="ai-card">
              <span>01</span>
              <h3>AI Chatbots</h3>
              <p>
                Custom AI chatbot solutions for websites, applications and
                business workflows.
              </p>
            </article>

            <article className="ai-card">
              <span>02</span>
              <h3>Business Automation</h3>
              <p>
                AI-assisted workflows designed to reduce repetitive manual
                processes and improve operational efficiency.
              </p>
            </article>

            <article className="ai-card">
              <span>03</span>
              <h3>Machine Learning</h3>
              <p>
                Machine learning solutions for suitable prediction,
                classification, recommendation and data-driven use cases.
              </p>
            </article>

            <article className="ai-card">
              <span>04</span>
              <h3>AI Integration</h3>
              <p>
                Connect AI capabilities with existing websites, applications,
                APIs, databases and business systems.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICES
      ===================================================== */}

      <section className="ai-services">
        <div className="ai-container">
          <div className="ai-section-heading">
            <span className="ai-section-label">
              OUR AI DEVELOPMENT SERVICES
            </span>

            <h2>AI Development Services</h2>

            <p>
              Select the AI capability that matches your business objective,
              technical environment and product requirements.
            </p>
          </div>

          <div className="ai-grid">
            <article className="ai-card">
              <h3>AI Chatbots & Virtual Assistants</h3>

              <p>
                Develop conversational AI solutions for customer support,
                information systems, websites and internal business use cases.
              </p>

              <ul>
                <li>Website AI chatbots</li>
                <li>Business assistants</li>
                <li>Knowledge-based AI systems</li>
              </ul>
            </article>

            <article className="ai-card">
              <h3>Machine Learning Solutions</h3>

              <p>
                Develop machine learning solutions for suitable data-driven
                applications and business requirements.
              </p>

              <ul>
                <li>Prediction systems</li>
                <li>Classification</li>
                <li>Recommendation use cases</li>
              </ul>
            </article>

            <article className="ai-card">
              <h3>AI Business Automation</h3>

              <p>
                Apply AI to suitable business workflows to help automate
                repetitive tasks and digital processes.
              </p>

              <ul>
                <li>Workflow automation</li>
                <li>Document processing</li>
                <li>AI-assisted operations</li>
              </ul>
            </article>

            <article className="ai-card">
              <h3>AI API & System Integration</h3>

              <p>
                Integrate AI functionality with existing applications,
                websites, APIs, databases and software systems.
              </p>

              <ul>
                <li>API integration</li>
                <li>Existing software integration</li>
                <li>Database-connected applications</li>
              </ul>
            </article>

            <article className="ai-card">
              <h3>AI-Powered Applications</h3>

              <p>
                Build custom applications where artificial intelligence is
                part of the core product functionality.
              </p>

              <ul>
                <li>AI web applications</li>
                <li>AI mobile applications</li>
                <li>Custom software solutions</li>
              </ul>
            </article>

            <article className="ai-card">
              <h3>Data & Intelligent Processing</h3>

              <p>
                Build systems that use AI to process, organize, classify or
                analyze suitable business data.
              </p>

              <ul>
                <li>Data classification</li>
                <li>Information extraction</li>
                <li>Intelligent data workflows</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          TECHNOLOGY
      ===================================================== */}

      <section className="ai-section ai-technology">
        <div className="ai-container">
          <div className="ai-section-heading">
            <span className="ai-section-label">
              AI TECHNOLOGY
            </span>

            <h2>AI Technologies for Different Project Requirements</h2>

            <p>
              The technology stack is selected according to the application's
              requirements, data, integrations, performance needs and
              deployment environment.
            </p>
          </div>

          <div className="ai-tech-grid">
            <span>Python</span>
            <span>Machine Learning</span>
            <span>AI APIs</span>
            <span>REST APIs</span>
            <span>Firebase</span>
            <span>Node.js</span>
            <span>JavaScript</span>
            <span>TypeScript</span>
            <span>Cloud Services</span>
            <span>Data Processing</span>
          </div>
        </div>
      </section>

      {/* =====================================================
          PROCESS
      ===================================================== */}

      <section className="ai-section ai-process">
        <div className="ai-container">
          <div className="ai-section-heading">
            <span className="ai-section-label">
              AI DEVELOPMENT PROCESS
            </span>

            <h2>How We Approach an AI Project</h2>
          </div>

          <div className="ai-process-grid">
            <article className="ai-card">
              <span>01</span>
              <h3>Understand the Problem</h3>
              <p>
                Identify the business problem, users, workflow and desired
                outcome before selecting an AI approach.
              </p>
            </article>

            <article className="ai-card">
              <span>02</span>
              <h3>Data & Requirements</h3>
              <p>
                Review available data, integrations, technical requirements
                and project constraints.
              </p>
            </article>

            <article className="ai-card">
              <span>03</span>
              <h3>Solution Design</h3>
              <p>
                Define the appropriate AI architecture, application workflow
                and integration approach.
              </p>
            </article>

            <article className="ai-card">
              <span>04</span>
              <h3>Development</h3>
              <p>
                Build the AI functionality and connect it with the required
                application or business system.
              </p>
            </article>

            <article className="ai-card">
              <span>05</span>
              <h3>Testing</h3>
              <p>
                Test the application workflow, integrations and AI behavior
                against the agreed project requirements.
              </p>
            </article>

            <article className="ai-card">
              <span>06</span>
              <h3>Deployment & Support</h3>
              <p>
                Deploy the solution and continue with agreed improvements,
                updates and technical support.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          PRICING
      ===================================================== */}

      <section className="ai-pricing">
        <div className="ai-container">
          <div className="ai-section-heading">
            <span className="ai-section-label">
              AI PROJECT PRICING
            </span>

            <h2>AI Development Cost</h2>

            <p>
              AI project pricing varies according to functionality, data,
              integrations, infrastructure, application requirements and
              overall complexity. We provide project-specific quotations after
              reviewing the requirements.
            </p>
          </div>

          <div className="ai-grid">
            <article className="ai-card">
              <span>01</span>

              <h3>AI Starter Project</h3>

              <p>
                Suitable for smaller AI features, chatbot implementations or
                focused automation requirements.
              </p>

              <strong>Custom Quote</strong>
            </article>

            <article className="ai-card ai-pricing-featured">
              <span>02</span>

              <h3>Business AI Solution</h3>

              <p>
                AI applications involving multiple features, integrations,
                workflows or business systems.
              </p>

              <strong>Custom Quote</strong>
            </article>

            <article className="ai-card">
              <span>03</span>

              <h3>Advanced AI Solution</h3>

              <p>
                Larger AI projects involving complex workflows, specialized
                requirements, multiple integrations or custom architecture.
              </p>

              <strong>Project Based</strong>
            </article>
          </div>

          <div className="ai-pricing-note">
            <p>
              <strong>Need an AI project estimate?</strong> Send us your
              requirements and we can review the scope before discussing the
              project quotation.
            </p>

            <button
              onClick={handleBooking}
              className="ai-btn ai-btn-primary"
            >
              Request AI Estimate
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          WORLDWIDE SERVICES
      ===================================================== */}

      <section className="ai-section ai-worldwide">
        <div className="ai-container">
          <div className="ai-worldwide-card">
            <div>
              <span className="ai-section-label">
                WORLDWIDE AI DEVELOPMENT
              </span>

              <h2>
                AI Solutions for Clients Worldwide
              </h2>

              <p>
                SamSoftix provides remote AI development and automation
                services for businesses, startups, organizations and digital
                product owners worldwide.
              </p>

              <p>
                Based in Pakistan, we collaborate remotely with international
                clients to plan, develop, integrate and deploy AI-powered
                digital solutions according to their project requirements.
              </p>
            </div>

            <div className="ai-market-tags">
              <span>Worldwide</span>
              <span>Remote AI Development</span>
              <span>Pakistan</span>
              <span>International Clients</span>
              <span>Remote Collaboration</span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          GOOGLE BUSINESS
      ===================================================== */}

      <section className="ai-google">
        <div className="ai-container">
          <h2>SamSoftix on Google</h2>

          <p>
            Learn more about SamSoftix, our business location and available
            customer information through our Google Business Profile.
          </p>

          <a
            href="https://maps.app.goo.gl/wRSiYJk2KqDbuebU6"
            target="_blank"
            rel="noopener noreferrer"
            className="ai-btn ai-btn-secondary"
          >
            View Google Business Profile
          </a>
        </div>
      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <section className="ai-section ai-faq">
        <div className="ai-container">
          <div className="ai-section-heading">
            <span className="ai-section-label">
              FREQUENTLY ASKED QUESTIONS
            </span>

            <h2>AI Solutions FAQ</h2>
          </div>

          <div className="ai-faq-list">
            <article className="ai-faq-item">
              <h3>
                Does SamSoftix provide AI development services worldwide?
              </h3>

              <p>
                Yes. SamSoftix provides remote AI development and automation
                services for clients worldwide.
              </p>
            </article>

            <article className="ai-faq-item">
              <h3>
                What AI solutions can SamSoftix develop?
              </h3>

              <p>
                We can work on AI chatbots, virtual assistants, business
                automation, machine learning applications, AI integrations and
                custom AI-powered software according to project requirements.
              </p>
            </article>

            <article className="ai-faq-item">
              <h3>
                Can AI be integrated into an existing website or software?
              </h3>

              <p>
                Yes. AI functionality can be integrated into suitable
                websites, applications, APIs, databases and business systems.
              </p>
            </article>

            <article className="ai-faq-item">
              <h3>
                How much does an AI project cost?
              </h3>

              <p>
                Pricing depends on the project's features, data requirements,
                integrations, infrastructure and complexity. Contact SamSoftix
                for a project-specific estimate.
              </p>
            </article>

            <article className="ai-faq-item">
              <h3>
                Can you build a custom AI chatbot for my business?
              </h3>

              <p>
                Yes. SamSoftix can develop custom AI chatbot solutions for
                websites, applications and business workflows based on the
                required functionality.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="ai-cta">
        <div className="ai-container">
          <div className="ai-cta-card">
            <span className="ai-section-label">
              READY TO EXPLORE AI?
            </span>

            <h2>
              Let's Discuss Your AI Project
            </h2>

            <p>
              Tell us about your business, workflow, application or AI idea.
              SamSoftix can review your requirements and discuss a suitable
              development approach.
            </p>

            <div className="ai-cta-actions">
              <button
                onClick={handleBooking}
                className="ai-btn ai-btn-primary"
              >
                Start Your AI Project
              </button>

              <a
                href="https://wa.me/923320472184"
                target="_blank"
                rel="noopener noreferrer"
                className="ai-btn ai-btn-secondary"
              >
                WhatsApp SamSoftix
              </a>
            </div>

            <p className="ai-contact-line">
              Wan • Sambrial • Sialkot • Pakistan • Serving Clients Worldwide
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

export default AISolutions;